import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, storytellerAPI, encryptionAPI } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [activeProfileId, setActiveProfileIdState] = useState(
    localStorage.getItem('legacylens_active_profile')
  );
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('legacylens_token'));
  const [encryptionSession, setEncryptionSessionState] = useState(
    localStorage.getItem('legacylens_enc_session')
  );

  const activeProfile = profiles.find(p => p.id === activeProfileId) || profiles[0] || null;

  // Keep this for backwards compat — returns first profile
  const profile = activeProfile;

  const setEncryptionSession = useCallback((sessionId) => {
    if (sessionId) {
      localStorage.setItem('legacylens_enc_session', sessionId);
    } else {
      localStorage.removeItem('legacylens_enc_session');
    }
    setEncryptionSessionState(sessionId);
  }, []);

  const clearEncryptionSession = useCallback(() => {
    localStorage.removeItem('legacylens_enc_session');
    setEncryptionSessionState(null);
  }, []);

  // Listen for encryption session expiry events from the API interceptor
  useEffect(() => {
    const handler = () => {
      setEncryptionSessionState(null);
    };
    window.addEventListener('encryption-session-expired', handler);
    return () => window.removeEventListener('encryption-session-expired', handler);
  }, []);

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await storytellerAPI.list();
      setProfiles(res.data || []);
      // If we have profiles but no active, select first
      if (res.data?.length > 0) {
        const savedId = localStorage.getItem('legacylens_active_profile');
        const exists = res.data.find(p => p.id === savedId);
        if (!exists) {
          setActiveProfileIdState(res.data[0].id);
          localStorage.setItem('legacylens_active_profile', res.data[0].id);
        }
      }
    } catch {
      setProfiles([]);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      if (token) {
        try {
          const res = await authAPI.me();
          setUser(res.data);
          await fetchProfiles();
        } catch {
          logout();
        }
      }
      setLoading(false);
    };
    init();
  }, [token, fetchProfiles]);

  const login = async (tokenVal, userData) => {
    // Clear stale state from previous user before setting new credentials
    localStorage.removeItem('legacylens_active_profile');
    setActiveProfileIdState(null);
    setProfiles([]);

    localStorage.setItem('legacylens_token', tokenVal);
    localStorage.setItem('legacylens_user', JSON.stringify(userData));
    setToken(tokenVal);
    setUser(userData);

    // Eagerly fetch profiles for the new user
    try {
      const res = await storytellerAPI.list();
      const fetched = res.data || [];
      setProfiles(fetched);
      if (fetched.length > 0) {
        setActiveProfileIdState(fetched[0].id);
        localStorage.setItem('legacylens_active_profile', fetched[0].id);
      }
    } catch {
      setProfiles([]);
    }
  };

  const logout = () => {
    // Invalidate server-side encryption session if active
    const encSession = localStorage.getItem('legacylens_enc_session');
    const profileId = localStorage.getItem('legacylens_active_profile');
    if (encSession && profileId) {
      encryptionAPI.lock(profileId).catch(() => {});
    }
    localStorage.removeItem('legacylens_token');
    localStorage.removeItem('legacylens_user');
    localStorage.removeItem('legacylens_active_profile');
    localStorage.removeItem('legacylens_enc_session');
    setToken(null);
    setUser(null);
    setProfiles([]);
    setActiveProfileIdState(null);
    setEncryptionSessionState(null);
  };

  const setActiveProfile = (id) => {
    setActiveProfileIdState(id);
    localStorage.setItem('legacylens_active_profile', id);
    // Clear encryption session when switching profiles
    clearEncryptionSession();
  };

  const refreshProfiles = async () => {
    await fetchProfiles();
  };

  // backwards compat alias
  const refreshProfile = refreshProfiles;

  // Centralized permission checks
  const isOwnProfile = !!(activeProfile && user && (
    activeProfile.gmail === user.email?.toLowerCase() ||
    activeProfile.claimed_by === user.id
  ));
  const isManager = !!(activeProfile && user && !isOwnProfile && (
    activeProfile.created_by === user.id ||
    (activeProfile.managers || []).includes(user.id)
  ));
  const canStartSession = isOwnProfile || isManager;

  return (
    <AuthContext.Provider value={{
      user, profiles, activeProfile, profile, loading, token,
      encryptionSession, setEncryptionSession, clearEncryptionSession,
      login, logout, setActiveProfile, refreshProfiles, refreshProfile,
      isOwnProfile, isManager, canStartSession,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
